import { useQuery } from "@tanstack/react-query";
import {
  productUsageRateListSchema,
  ProductUsageRateSchema,
  subscriptionsPerProductListSchema,
  SubscriptionsPerProductSchema,
  geographicDistributionListSchema,
  GeographicDistributionSchema,
  AzureMapPerCitySchema,
  azureMapPerCityListSchema,
  DashboardCountsSchema,
  dashboardCountsSchema,
} from "./schema";
import { useGet } from "../../../common/api-service";


/* ---------------- PRODUCT USAGE RATE ---------------- */

export const useGetDashboardCounts = (dateRange?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const { get } = useGet();

  const queryString = new URLSearchParams({
    ...(dateRange?.startDate && {
      startDate: dateRange.startDate.toISOString(),
    }),
    ...(dateRange?.endDate && {
      endDate: dateRange.endDate.toISOString(),
    }),
  }).toString();

  const url = `/api/dashboard/product-usage-count${queryString ? `?${queryString}` : ""}`;

  return useQuery<DashboardCountsSchema>({
    queryKey: [
      "dashboard-counts",
      dateRange?.startDate?.toISOString(),
      dateRange?.endDate?.toISOString(),
    ],
    queryFn: async () => {
      const res = await get<DashboardCountsSchema>(url);
      return dashboardCountsSchema.parse(res);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};

/* ---------------- SUBSCRIPTIONS PER PRODUCT ---------------- */

export const useGetSubscriptionsPerProduct = (dateRange?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const { get } = useGet();

  const queryString = new URLSearchParams({
    ...(dateRange?.startDate && {
      startDate: dateRange.startDate.toISOString(),
    }),
    ...(dateRange?.endDate && {
      endDate: dateRange.endDate.toISOString(),
    }),
  }).toString();

  const url = `/api/dashboard/subscriptions-per-product${
    queryString ? `?${queryString}` : ""
  }`;

  return useQuery<SubscriptionsPerProductSchema[]>({
    queryKey: [
      "dashboard-subscriptions-per-product",
      dateRange?.startDate?.toISOString(),
      dateRange?.endDate?.toISOString(),
    ],
    queryFn: async () => {
      const res = await get<SubscriptionsPerProductSchema[]>(url);
      return subscriptionsPerProductListSchema.parse(res);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};

/* ---------------- GEOGRAPHIC DISTRIBUTION ---------------- */

export const useGetGeographicDistribution = () => {
  const { get } = useGet();
  const url = `/api/dashboard/geographic-distribution-per-region`;

  return useQuery<GeographicDistributionSchema[]>({
    queryKey: ["dashboard-geographic-distribution"],
    queryFn: async () => {
      const res = await get<GeographicDistributionSchema[]>(url);
      return geographicDistributionListSchema.parse(res);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};

export const useGetFacilityDistributionPerCity = (filter: number) => {
  const { get } = useGet();

  const url = `/api/dashboard/geographic-distribution-per-city?filter=${filter}`;

  return useQuery<AzureMapPerCitySchema[]>({
    queryKey: ["dashboard-city-distribution", filter],
    queryFn: async () => {
      const res = await get<AzureMapPerCitySchema[]>(url);
      return azureMapPerCityListSchema.parse(res);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
};
